/**
 * Building transfer — content-type sniffing (pure).
 *
 * Photo Storage paths carry no file extension (they end in the photo key), so
 * the import cannot infer a MIME type from the path. The generateThumbnails
 * trigger hard-skips any object whose contentType does not start with
 * "image/", which means an upload stamped application/octet-stream never gets
 * thumbnails. This sniffs the real type from the file's magic bytes instead.
 */

const ascii = (bytes: Uint8Array, start: number, length: number): string => {
    let out = '';
    for (let i = start; i < start + length && i < bytes.length; i++) {
        out += String.fromCharCode(bytes[i]);
    }
    return out;
};

const HEIC_BRANDS = new Set(['heic', 'heix', 'hevc', 'heim', 'heis', 'hevm', 'hevs']);
const HEIF_BRANDS = new Set(['mif1', 'msf1']);

/**
 * Identify a file's MIME type from its first bytes (at least 16 are needed
 * for the ISO-BMFF/ftyp formats). Returns null when the signature is unknown —
 * the caller falls back to extension-based inference.
 */
export function sniffContentType(bytes: Uint8Array): string | null {
    if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
        return 'image/jpeg';
    }
    if (
        bytes.length >= 4 &&
        bytes[0] === 0x89 &&
        bytes[1] === 0x50 &&
        bytes[2] === 0x4e &&
        bytes[3] === 0x47
    ) {
        return 'image/png';
    }
    if (ascii(bytes, 0, 3) === 'GIF') {
        return 'image/gif';
    }
    if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP') {
        return 'image/webp';
    }
    if (ascii(bytes, 4, 4) === 'ftyp') {
        const brand = ascii(bytes, 8, 4);
        if (HEIC_BRANDS.has(brand)) return 'image/heic';
        if (HEIF_BRANDS.has(brand)) return 'image/heif';
        if (brand === 'qt  ') return 'video/quicktime';
        return 'video/mp4';
    }
    if (ascii(bytes, 0, 4) === '%PDF') {
        return 'application/pdf';
    }
    return null;
}
